"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const styles_1 = require("@mui/material/styles");
const base_1 = require("./base");
// Colors in this palette _not_ derived from brandColors are pulled from figma.
// Note: this palette is merged with the default MUI palette via createTheme.
const palette = {
    brand: base_1.brandColors,
    common: {
        white: "white",
        black: "black",
        grey: "grey",
    },
    primary: { main: base_1.brandColors.primary.main },
    secondary: { main: "rgba(14, 15, 24, 0.04)" },
    error: {
        main: base_1.brandColors.red.main,
    },
    warning: {
        main: base_1.brandColors.yellow.main,
    },
    info: {
        light: "#A8AFCE",
        main: "#79809D",
        dark: "#4C546F",
    },
    success: {
        main: base_1.brandColors.green.main,
    },
    background: {
        paper: "#ffffff",
        default: "rgba(245, 247, 250, 1)",
    },
    text: {
        primary: "rgba(14, 15, 24, 1)",
        secondary: "rgba(14, 15, 24, 0.64)",
        disabled: "rgba(14, 15, 24, 0.32)",
    },
};
const decodableBits = {
    contentArea: {
        maxWidth: "1024px",
    },
    sheetWidth: {
        small: "600px",
        medium: "800px",
        large: "1024px",
    },
    layoutParameters: {
        // Gives enough clearance for the "main" component to coexist with
        // the app bar & subheader, if present, and have some bottom padding
        // (to avoid collisions with horizontal scroll bars) on a 1080 screen.
        //
        // TODO: Sure can't wait to get all of the pages switched over to a grid
        // layout / on the new page templates to better manage vertical space
        // and get rid of this horrible magic number. Please forgive me for making
        // you look at this.
        primaryComponentMaxHeight: "calc(100vh - 264px)",
    },
    tags: {
        chip: {
            fontSize: "0.8215rem",
        },
    },
};
// todo: https://mui.com/customization/theme-components/#adding-new-component-variants
// We're using the variants API which is deprecated. We should switch to styleOverrides functions.
// todo: extract "base" theme object & mixin to light & dark modes
const decodableThemeOptions = {
    palette,
    decodable: decodableBits,
    // Note: Inter & "IBM Plex Mono" must be loaded by the application!
    // We should be self-hosting these, but for convenience they can be loaded from Google's CDN:
    //
    //   <link rel="stylesheet" href="https://fonts.googleapis.com/css?family=Inter">
    //   <link rel="stylesheet" href="https://fonts.googleapis.com/css?family=IBM+Plex+Mono">
    typography: {
        fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
        code: {
            fontFamily: '"IBM Plex Mono", monospace',
        },
    },
    components: {
        MuiAppBar: {
            styleOverrides: {
                root: {
                    backgroundColor: palette.common.white,
                    color: palette.primary.main,
                    "& .MuiToolbar-root": {
                        minHeight: 0,
                        height: "4.2rem",
                        padding: 0,
                        overflow: "hidden",
                    },
                    "& .MuiTabs-root": {
                        height: "100%",
                    },
                    "& .MuiTab-root": {
                        fontSize: ".9rem",
                        padding: "0 1.5ch",
                    },
                    "& .MuiTabs-flexContainer": {
                        height: "100%",
                    },
                    "& .MuiTabs-indicator": {
                        height: "2px",
                    },
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    backgroundColor: palette.common.white,
                },
            },
        },
        MuiTextField: {
            defaultProps: {
                autoComplete: "off",
                InputLabelProps: {
                    shrink: true,
                },
            },
        },
        MuiButton: {
            defaultProps: {
                disableElevation: true,
            },
            styleOverrides: {
                root: {
                    textTransform: "none",
                    fontSize: "0.9rem",
                    fontWeight: "400",
                },
            },
            variants: [
                {
                    props: { color: "secondary" },
                    style: {
                        color: palette.primary.main,
                    },
                },
            ],
        },
        MuiButtonGroup: {
            defaultProps: {
                disableElevation: true,
            },
            styleOverrides: {
                root({ ownerState }) {
                    const overrides = {};
                    if (ownerState.color == "primary") {
                        overrides["& .MuiButtonGroup-grouped:not(:last-of-type)"] = {
                            borderColor: palette.common.grey,
                        };
                    }
                    return overrides;
                },
            },
        },
        MuiCheckbox: {
            styleOverrides: {
                root: {
                    "&.Mui-checked.MuiCheckbox-colorPrimary": {
                        color: palette.brand.blue.main,
                    },
                    "&.Mui-checked.MuiCheckbox-colorSecondary": {
                        // We're just increasing the opacity from the default
                        color: "rgba(14, 15, 24, 0.4)",
                    },
                },
            },
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    "& .MuiListItemIcon-root": {
                        minWidth: "3ch",
                    },
                },
            },
        },
        MuiToggleButton: {
            styleOverrides: {
                root: {
                    textTransform: "none",
                },
            },
        },
        MuiTabs: {
            styleOverrides: {
                root: {
                    "& .MuiButtonBase-root": {
                        textTransform: "none",
                    },
                    "& .Mui-selected": {
                        fontWeight: "500",
                    },
                },
            },
        },
        MuiTypography: {
            variants: [
                {
                    props: { variant: "body2" },
                    style: {
                        color: palette.text.secondary,
                    },
                },
            ],
        },
        MuiTableCell: {
            styleOverrides: {
                head: {
                    fontWeight: 600,
                    backgroundColor: palette.background.paper,
                    py: 0,
                },
                root: {
                    fontSize: "1rem",
                    height: "3.5em",
                },
            },
        },
        MuiLink: {
            styleOverrides: {
                root: {
                    color: base_1.brandColors.blue.main,
                    textDecoration: "none",
                },
            },
        },
        MuiAlert: {
            styleOverrides: {
                root: ({ ownerState, theme }) => {
                    if (ownerState.color == "grey") {
                        const color = theme.palette.primary.main;
                        return {
                            color,
                            "& .MuiAlert-icon": { color },
                            backgroundColor: theme.palette.secondary.main,
                        };
                    }
                    const color = theme.palette[ownerState.severity || "info"].contrastText;
                    return {
                        color,
                        "& .MuiAlert-icon": { color },
                        backgroundColor: theme.palette[ownerState.severity || "info"].main,
                        fontWeight: ownerState.severity == "error" ? "500" : undefined,
                    };
                },
            },
        },
        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    "& .MuiLink-root": {
                        color: palette.common.white,
                    },
                },
            },
            defaultProps: {
                arrow: true,
            },
        },
        MuiBadge: {
            styleOverrides: {
                badge: ({ ownerState }) => {
                    const base = { fontWeight: "500" };
                    if (ownerState.color == "primary") {
                        return Object.assign(Object.assign({}, base), { backgroundColor: palette.brand.blue.main, color: palette.brand.blue.contrastText });
                    }
                    if (ownerState.color == "secondary") {
                        return Object.assign(Object.assign({}, base), { 
                            // Default secondary color is transparent, which doesn't work for badges
                            // (you can barely see it). Use an opaque grey instead.
                            backgroundColor: "grey", color: "white" });
                    }
                    return base;
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    borderRadius: "4px",
                    fontWeight: 400,
                    fontSize: decodableBits.tags.chip.fontSize,
                },
                colorSecondary: ({ theme }) => ({
                    backgroundColor: theme.palette.grey[200],
                    color: theme.palette.grey[900],
                }),
                deleteIcon: (props) => {
                    if (props.ownerState.color === "secondary") {
                        return {
                            color: props.theme.palette.grey[400],
                            ":hover": {
                                color: palette.common.black,
                            },
                        };
                    }
                },
            },
        },
        MuiSlider: {
            styleOverrides: {
                track: {
                    color: "var(--de-color-blue)",
                },
                thumb: {
                    color: "var(--de-color-blue)",
                },
                mark: {
                    color: "white",
                },
            },
        },
    },
};
const lightTheme = (0, styles_1.createTheme)(decodableThemeOptions);
exports.default = lightTheme;
